import React from "react";
import { ArrowRight } from "lucide-react";
import Section from "@/components/foundation/Section";
import Card from "@/components/foundation/Card";
import FadeIn from "@/components/foundation/FadeIn";
import {
  Brain,
  Gauge,
  BarChart3,
  Waves,
  LayoutDashboard,
  FileLock2,
  ClipboardCheck,
  RadioTower,
  Sparkles,
} from "lucide-react";

const FEATURES = [
  {
    icon: Brain,
    title: "AI-Powered ZenScore",
    body: "Real-time funding readiness intelligence built from live business signals.",
  },
  {
    icon: Gauge,
    title: "Funding Readiness Insights",
    body: "Understand exactly where your business stands and what to improve next.",
  },
  {
    icon: BarChart3,
    title: "Revenue Analytics",
    body: "Detailed revenue breakdowns, growth velocity, and channel performance.",
  },
  {
    icon: Waves,
    title: "Cash Flow Trends",
    body: "Inflow / outflow trajectories with volatility and stability scoring.",
  },
  {
    icon: LayoutDashboard,
    title: "Funding Dashboard",
    body: "One unified view of applications, offers, and matching progress.",
  },
  {
    icon: FileLock2,
    title: "Secure Document Storage",
    body: "Encrypted vault for tax returns, licenses, and financial statements.",
  },
  {
    icon: ClipboardCheck,
    title: "Simple Application",
    body: "Pre-filled forms powered by connected banking data — minimal typing.",
  },
  {
    icon: RadioTower,
    title: "Real-Time Updates",
    body: "Live status changes for applications, matches, and partner responses.",
  },
  {
    icon: Sparkles,
    title: "Personalized Funding Offers",
    body: "Offers tailored to your business profile, tenure, and cash flow shape.",
  },
];

export const FeaturesSection = () => {
  return (
    <Section
      eyebrow="For Businesses"
      headline="Everything you need to understand your funding potential."
      subheadline="A complete toolkit for modern operators — from banking-level analytics to matched funding — all in one intelligent workspace."
      spacing="lg"
      className="relative"
    >
      <div
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        data-testid="features-section"
      >
        {FEATURES.map((f, i) => {
          const Icon = f.icon;
          return (
            <FadeIn key={f.title} delay={0.04 * i}>
              <Card
                variant="elevated"
                className="group relative flex h-full min-h-[196px] flex-col overflow-hidden p-6"
                data-testid={`feature-card-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-brand-emerald/25 bg-brand-emerald/10 text-brand-emerald transition-transform duration-300 group-hover:-translate-y-0.5">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <h4 className="mt-5 font-display text-lg font-semibold text-white">
                  {f.title}
                </h4>
                <p className="mt-1.5 flex-1 text-sm leading-relaxed text-text-secondary">
                  {f.body}
                </p>
                <span
                  aria-hidden="true"
                  className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-brand-emerald opacity-0 transition-all duration-300 group-hover:translate-x-1 group-hover:opacity-100"
                >
                  Learn more
                  <ArrowRight className="h-3 w-3" />
                </span>
              </Card>
            </FadeIn>
          );
        })}
      </div>
    </Section>
  );
};

export default FeaturesSection;
